// Monday digest preview — the Phase 5 gate.
//
// Left column: list of manager digests rendered for the target Monday.
// Right column: live HTML preview of the selected manager's email.
// Top right: "Send all" / "Send selected" actions.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type DigestSummary } from "../lib/api";

export function DigestPreview() {
  const qc = useQueryClient();
  const [targetMonday, setTargetMonday] = useState<string>(mostRecentMonday());
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["digest-preview", targetMonday],
    queryFn: () => api.digest.list(targetMonday),
  });

  const sendAll = useMutation({
    mutationFn: () => api.digest.sendAll(undefined, targetMonday),
    onSuccess: (data) => {
      alert(`Enqueued ${data.enqueued} digest${data.enqueued === 1 ? "" : "s"} (skipped ${data.skipped} dup).`);
      qc.invalidateQueries({ queryKey: ["digest-preview"] });
    },
  });

  const sendOne = useMutation({
    mutationFn: (managerId: string) => api.digest.sendAll([managerId], targetMonday),
    onSuccess: (data) => {
      alert(`Enqueued ${data.enqueued} (skipped ${data.skipped} dup).`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">Monday digest preview</h1>
        <div className="flex items-center gap-2 text-sm">
          <label>Target Monday:</label>
          <input
            type="date"
            className="border rounded px-2 py-1"
            value={targetMonday}
            onChange={(e) => setTargetMonday(e.target.value)}
          />
          <button
            className="px-3 py-1 rounded bg-blue-600 text-white"
            onClick={() => sendAll.mutate()}
            disabled={sendAll.isPending || (list.data?.digests?.length ?? 0) === 0}
          >
            {sendAll.isPending ? "Sending…" : "Send all"}
          </button>
        </div>
      </div>

      {list.isLoading ? <p>Generating…</p> : null}
      {list.error ? <p className="text-red-700 text-sm">{(list.error as Error).message}</p> : null}

      {list.data ? (
        <div className="grid grid-cols-3 gap-4">
          {/* Left: per-manager list */}
          <div className="col-span-1 border rounded bg-white">
            <div className="px-3 py-2 border-b font-medium text-sm">
              {list.data.digests?.length ?? 0} digest{(list.data.digests?.length ?? 0) === 1 ? "" : "s"}
            </div>
            <ul className="divide-y">
              {(list.data.digests ?? []).map((d: DigestSummary) => (
                <li
                  key={d.manager_id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${selectedId === d.manager_id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedId(d.manager_id)}
                >
                  <div className="font-medium">{d.manager_name}</div>
                  <div className="text-xs text-slate-600 truncate">{d.subject}</div>
                  <div className="text-xs text-slate-400 truncate">{d.manager_email}</div>
                </li>
              ))}
              {(list.data.digests ?? []).length === 0 ? (
                <li className="px-3 py-4 text-sm text-slate-500">
                  No managers with active pilots — nothing to send.
                </li>
              ) : null}
            </ul>
          </div>

          {/* Right: HTML preview iframe */}
          <div className="col-span-2 border rounded bg-white">
            {selectedId ? (
              <div>
                <div className="px-3 py-2 border-b flex items-center justify-between">
                  <span className="text-sm font-medium">Preview</span>
                  <button
                    className="text-xs px-2 py-1 rounded bg-emerald-600 text-white"
                    disabled={sendOne.isPending}
                    onClick={() => sendOne.mutate(selectedId)}
                  >
                    {sendOne.isPending ? "Sending…" : "Send only this one"}
                  </button>
                </div>
                <iframe
                  key={selectedId + targetMonday}
                  title="digest-preview"
                  src={`/api/admin/digest-preview/${selectedId}?format=html&target_monday=${targetMonday}`}
                  className="w-full"
                  style={{ height: "75vh", border: 0 }}
                />
              </div>
            ) : (
              <p className="p-6 text-slate-500 text-sm">Pick a manager on the left to preview.</p>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function mostRecentMonday(): string {
  const d = new Date();
  const wd = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (wd - 1));
  return d.toISOString().slice(0, 10);
}
